import { useCallback, useMemo, useRef, useState } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { fareClassToUrlMap, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { parseAsBoolean, useQueryState } from 'nuqs'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { useIntl } from 'react-intl'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import MaldivesCharterCalendarBanner from 'components/Flights/CharterBundle/MaldivesCharterCalendarBanner'
import { MaldivesCharterCalendarModalBody } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesCharterCalendarModalLayout'

interface StateProps {
  offer: App.HotelOffer
  duration: number
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  charterBundleRoute: App.CharterBundleRoute
  airport?: App.Airport
}

interface Props {
  fareClass: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  onClose?: () => void
}

/**
 * Charter Bundle Calendar Modal for Mobile
 * Fetches its own data using useOfferCharterCalendarData hook
 * Follows the pattern of OfferCalendarSidebarDatePicker - dispatches dates internally
 */
function FlashMobileStickyHeaderFiltersCharterCalendarModal(props: Props & StateProps) {
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    charterBundleRoute,
    fareClass,
    priceMode,
    onDurationChange,
    onClose,
    airport,
  } = props

  const intl = useIntl()
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const modalElement = useModalElementContext()
  const dispatch = useOfferPageDispatch()
  const modalBodyRef = useRef<HTMLDivElement | null>(null)

  // Read luxPlus state from URL (same as parent component)
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(true),
  )
  const { fareClassFromUrl, setFareClassUrl, cabinFilter, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly, infiniteMemberOnly, setInfiniteMemberOnly } = useCharterFilterUrlState()
  const selectedFareClass = fareClassFromUrl ?? fareClass
  const fareClassForSearch = toBaseFareClass(selectedFareClass)

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass: fareClassForSearch,
    disabled: rooms.length === 0,
    feederOriginAirportCode: airport?.code,
  })

  // Filter dateRangesByMonth by selected nights
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, duration),
    [allDateRangesByMonth, duration],
  )

  // Manage selected month state internally
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format(ISO_DATE_SHORT_FORMAT) : moment().format(ISO_DATE_SHORT_FORMAT),
  )

  // Fetch charter calendar data
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass: fareClassForSearch,
    dateRangesByMonth,
    charterBundleRoute,
    feederOriginAirportCode: airport?.code,
  })

  // Sync fare class to URL so reward selections persist
  const setFareClassWithUrl = useCallback((fc: FareClassWithReward | null) => {
    if (fc) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: fc })
      setFareClassUrl(fareClassToUrlMap[fc])
    }
  }, [dispatch, setFareClassUrl])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string, fareClass?: FareClassWithReward }) => {
    const selectedCabin = option.fareClass ?? fareClass
    if (selectedCabin) {
      setFareClassWithUrl(selectedCabin)
    }
    // Use checkIn and checkOut (inner dates) for hotel queries
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: option.checkIn,
      checkOut: option.checkOut,
    })
    dispatch({ type: OfferPageActions.closeHeaderFilter })
    onClose?.()
    modalElement.resolve()
  }, [dispatch, onClose, modalElement, fareClass, setFareClassWithUrl])

  const handlePriceModeChange = useCallback((nextPriceMode: 'total' | 'perPerson') => {
    dispatch({ type: OfferPageActions.setPriceMode, priceMode: nextPriceMode })
  }, [dispatch])

  const handleClose = useCallback(() => {
    onClose?.()
    modalElement.resolve()
  }, [modalElement, onClose])

  const selectedOptionId = useMemo(() => {
    return checkInDate ? `${checkInDate}-${duration}n` : undefined
  }, [checkInDate, duration])

  // Filter duration options to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return charterBundleDurationOptions.filter(option => {
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [offer.packages, charterBundleDurationOptions])
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, rooms)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)

  const modalTitle = intl.formatMessage({
    id: 'yCqcj91896wnDdzZqqpa',
    defaultMessage: 'Select your trip dates',
  })

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={handleClose}
      />
      <MaldivesCharterCalendarModalBody ref={modalBodyRef}>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClassForSearch}
            selectedFareClass={selectedFareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="search"
            offer={offer}
            cabinFilter={cabinFilter}
            rewardSeatsOnly={rewardSeatsOnly}
            infiniteMemberOnly={infiniteMemberOnly}
            onFareClassChange={(fc) => {
              dispatch({ type: OfferPageActions.setFareClass, fareClass: fc })
              setFareClassUrl(fareClassToUrlMap[fc])
            }}
            headerActions={<CharterBundleCalendarActions
              nights={duration}
              onDurationChange={onDurationChange}
              durationOptions={filteredDurationOptions}
              luxPlusEnabled={luxPlusEnabled}
              onLuxPlusToggle={setLuxPlusEnabled}
              cabinFilter={cabinFilter}
              onCabinFilterChange={setCabinFilter}
              availableFareClasses={allowedFareClassesForModal}
              rewardSeatsOnly={rewardSeatsOnly}
              onRewardSeatsOnlyChange={setRewardSeatsOnly}
              infiniteMemberOnly={infiniteMemberOnly}
              onInfiniteMemberOnlyChange={setInfiniteMemberOnly}
              priceMode={priceMode}
              onPriceModeChange={handlePriceModeChange}
            />}
            stickyHeaderBanner={<MaldivesCharterCalendarBanner rooms={rooms} duration={duration} charterBundleRoute={charterBundleRoute} flush />}
            scrollContainerRef={modalBodyRef}
            feederOriginAirportCode={airport?.code}
          />
        </ModalContent>
      </MaldivesCharterCalendarModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer!,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  charterBundleRoute: state.charterBundleRoute!,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
}), FlashMobileStickyHeaderFiltersCharterCalendarModal)
